import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { FolderPlus, FolderOpen, Trash2, X, Share2 } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import ShareFolderDialog from "@/components/app/ShareFolderDialog";

const COLORS = ["#0A0A0A", "#009966", "#FF3333", "#FFB800", "#8A2BE2", "#5C5C5C"];

export default function Folders() {
  const [folders, setFolders] = useState([]);
  const [name, setName] = useState("");
  const [color, setColor] = useState("#0A0A0A");
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = () => api.get("/folders").then(r => setFolders(r.data)).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!name.trim()) return;
    try {
      await api.post("/folders", { name: name.trim(), color });
      toast.success("Cartella creata");
      setOpen(false); setName(""); setColor("#0A0A0A");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
  };

  const remove = async (f) => {
    if (!window.confirm(`Eliminare la cartella "${f.name}"? I documenti verranno spostati in "Altro".`)) return;
    try {
      await api.delete(`/folders/${f.id}`);
      toast.success("Cartella eliminata");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Impossibile eliminare"); }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 py-6 md:py-8">
      <div className="flex items-start justify-between mb-8">
        <div>
          <div className="overline text-[#0A0A0A] mb-3">— ORGANIZZAZIONE</div>
          <h1 className="font-display font-bold text-4xl tracking-tighter">Cartelle</h1>
          <p className="text-sm text-[#5C5C5C] mt-2">Le cartelle predefinite sono create automaticamente. Puoi aggiungerne di personalizzate.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-folder-btn" className="rounded-xl bg-[#0A0A0A] hover:bg-[#1A1A1A] text-white h-10">
              <FolderPlus className="w-4 h-4 mr-2" /> Nuova cartella
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-xl">
            <DialogHeader>
              <DialogTitle className="font-display font-bold tracking-tight">Nuova cartella</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div>
                <Label htmlFor="folder-name" className="text-xs uppercase tracking-widest">Nome</Label>
                <Input data-testid="folder-name-input" id="folder-name" value={name} onChange={e => setName(e.target.value)}
                  className="rounded-xl mt-2 bg-white h-10 focus-visible:ring-[#0A0A0A]" placeholder="Es. Bollette, Casa..." />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest">Colore</Label>
                <div className="flex gap-2 mt-2">
                  {COLORS.map(c => (
                    <button
                      key={c}
                      data-testid={`color-swatch-${c.replace('#','')}`}
                      type="button"
                      onClick={() => setColor(c)}
                      style={{ background: c }}
                      className={`w-8 h-8 rounded-xl transition-transform hover:scale-110 ${color === c ? "ring-2 ring-offset-2 ring-black" : ""}`}
                    />
                  ))}
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button data-testid="folder-create-cancel" variant="outline" onClick={() => setOpen(false)} className="rounded-xl border-black/20">Annulla</Button>
              <Button data-testid="folder-create-submit" onClick={create} className="rounded-xl bg-[#0A0A0A] hover:bg-[#1A1A1A] text-white">Crea</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="text-sm text-[#8A8A8A]">Caricamento…</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {folders.map((f, i) => (
            <motion.div
              key={f.id}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.03 }}
              className="bg-white border border-black/10 rounded-xl hover:border-black/25 transition-colors group"
            >
              <Link
                to={`/app/documenti?folder=${f.id}`}
                data-testid={`folder-card-${f.id}`}
                className="block p-6"
              >
                <div className="flex items-start justify-between mb-6">
                  <div
                    className="w-10 h-10 flex items-center justify-center rounded-xl"
                    style={{ background: `${f.color}15` }}
                  >
                    <FolderOpen className="w-5 h-5" style={{ color: f.color }} strokeWidth={1.75} />
                  </div>
                  <div className="overline font-mono-plex text-[#8A8A8A]">{f.document_count} doc.</div>
                </div>
                <div className="font-display font-bold text-xl tracking-tight mb-1">{f.name}</div>
                <div className="text-xs text-[#8A8A8A]">
                  Creata il {new Date(f.created_at).toLocaleDateString("it-IT")}
                </div>
              </Link>
              <div className="border-t border-black/5 px-6 py-3 flex justify-between items-center">
                <Link to={`/app/documenti?folder=${f.id}`} className="text-xs text-[#0A0A0A] hover:underline">Apri →</Link>
                <div className="flex items-center gap-1">
                  <ShareFolderDialog
                    folder={f}
                    trigger={
                      <button
                        data-testid={`folder-share-${f.id}`}
                        className="text-[#8A8A8A] hover:text-[#0A0A0A] transition-colors p-1"
                        title="Condividi con commercialista"
                      >
                        <Share2 className="w-3.5 h-3.5" />
                      </button>
                    }
                  />
                  <button
                    data-testid={`folder-delete-${f.id}`}
                    onClick={(e) => { e.preventDefault(); remove(f); }}
                    className="text-[#8A8A8A] hover:text-[#FF3333] transition-colors p-1"
                    title="Elimina"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}
